import { transferHandlers, wrap, type Remote } from "comlink";
import { useEffect, useRef, useState } from "react";
import type {
  LazyHttpDatabase,
  SplitFileConfig,
  SqliteComlinkMod,
} from "sql.js-httpvfs/dist/sqlite.worker";
import { isVolumeFilePath } from "../lib/apiPaths";
import { META_SEP } from "./MetaSeparator";
import { TableLoading, TablePreview, TableStatus } from "./TablePreview";

const SQLITE_MAX_BYTES_TO_READ = 128 * 1024 * 1024;
const SQLITE_TABLE_LIMIT = 100;
const SQLITE_PAGE_SIZE = 50;
const SQLITE_REQUEST_CHUNK_SIZE = 4096;

type SqlValue = number | string | Uint8Array | null;
type SqlExecResult = { columns: string[]; values: SqlValue[][] };
type SqlColumn = {
  name: string;
  type: string;
  primaryKey: boolean;
  notNull: boolean;
};
type SqlTable = {
  name: string;
  type: string;
  columns: SqlColumn[];
};
type SqlSchema = {
  totalTables: number;
  tables: SqlTable[];
};
type SqlRow = {
  key: string;
  values: Record<string, SqlValue>;
};
type SqlResult = {
  columns: string[];
  rows: SqlRow[];
  hasNext: boolean;
};
type SqliteWorker = { db: Remote<LazyHttpDatabase>; terminate: () => void };
type DatabaseState =
  | { status: "idle" | "loading" | "too-expensive" | "missing-url" }
  | { status: "error"; message: string }
  | { status: "ready"; schema: SqlSchema };
type ResultState =
  | { status: "idle" | "loading" | "too-expensive" }
  | { status: "error"; message: string }
  | { status: "ready"; result: SqlResult };

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function quoteSqlIdentifier(identifier: string) {
  return `"${identifier.replaceAll('"', '""')}"`;
}

async function execRows(
  db: Remote<LazyHttpDatabase>,
  sql: string,
  params: SqlValue[] = []
): Promise<Record<string, SqlValue>[]> {
  const [result] = (await db.exec(sql, params)) as SqlExecResult[];
  if (!result) return [];
  return result.values.map((values) =>
    Object.fromEntries(
      result.columns.map((column, i) => [column, values[i] ?? null])
    )
  );
}

async function sqlNumber(
  db: Remote<LazyHttpDatabase>,
  sql: string,
  params?: SqlValue[]
) {
  const [row] = await execRows(db, sql, params);
  const value = row?.[Object.keys(row)[0] ?? ""];
  return typeof value === "number" ? value : Number(value ?? 0);
}

async function readSchema(db: Remote<LazyHttpDatabase>): Promise<SqlSchema> {
  const totalTables = await sqlNumber(
    db,
    "SELECT COUNT(*) FROM sqlite_schema WHERE type IN ('table', 'view') AND name NOT LIKE 'sqlite_%'"
  );
  const tableRows = await execRows(
    db,
    "SELECT type, name FROM sqlite_schema WHERE type IN ('table', 'view') AND name NOT LIKE 'sqlite_%' ORDER BY name LIMIT ?",
    [SQLITE_TABLE_LIMIT]
  );
  const tables = await Promise.all(
    tableRows.map(async (row) => {
      const name = String(row.name);
      const columns = await execRows(
        db,
        `PRAGMA table_xinfo(${quoteSqlIdentifier(name)})`
      );
      return {
        name,
        type: String(row.type),
        columns: columns.map((column) => ({
          name: String(column.name),
          type: String(column.type ?? ""),
          primaryKey: Number(column.pk ?? 0) > 0,
          notNull: Number(column.notnull ?? 0) === 1,
        })),
      };
    })
  );

  return { totalTables, tables };
}

function escapeLike(value: string) {
  return value
    .replaceAll("\\", "\\\\")
    .replaceAll("%", "\\%")
    .replaceAll("_", "\\_");
}

function formatSqlValue(value: SqlValue) {
  if (value === null) return "NULL";
  if (value instanceof Uint8Array) return `BLOB ${formatBytes(value.length)}`;
  return String(value);
}

function rowKey(row: Record<string, SqlValue>, offset: number) {
  return `${offset}:${Object.entries(row)
    .map(([column, value]) => `${column}=${formatSqlValue(value)}`)
    .join("|")}`;
}

async function queryTable(
  db: Remote<LazyHttpDatabase>,
  table: SqlTable,
  searchColumn: string | null,
  search: string,
  page: number
): Promise<SqlResult> {
  const quotedTable = quoteSqlIdentifier(table.name);
  const trimmedSearch = search.trim();
  // A null searchColumn searches every column via an OR of LIKEs.
  const searchColumns = searchColumn
    ? [searchColumn]
    : table.columns.map((column) => column.name);
  const conditions = trimmedSearch
    ? searchColumns.map(
        (name) => `CAST(${quoteSqlIdentifier(name)} AS TEXT) LIKE ? ESCAPE '\\'`
      )
    : [];
  const where = conditions.length ? ` WHERE ${conditions.join(" OR ")}` : "";
  const searchParams = conditions.map(() => `%${escapeLike(trimmedSearch)}%`);
  const offset = page * SQLITE_PAGE_SIZE;
  const rows = await execRows(
    db,
    `SELECT * FROM ${quotedTable}${where} LIMIT ? OFFSET ?`,
    [...searchParams, SQLITE_PAGE_SIZE + 1, offset]
  );
  return {
    columns: table.columns.map((column) => column.name),
    rows: rows.slice(0, SQLITE_PAGE_SIZE).map((row, i) => ({
      key: rowKey(row, offset + i),
      values: row,
    })),
    hasNext: rows.length > SQLITE_PAGE_SIZE,
  };
}

function openUrl(url: string) {
  return new URL(url, location.href).href;
}

function sqlitePreviewUrl(url: string) {
  const previewUrl = new URL(url, location.href);
  if (isVolumeFilePath(previewUrl.pathname)) {
    previewUrl.searchParams.set("sqlite", "1");
  }
  return previewUrl.href;
}

function sqliteErrorMessage(err: unknown) {
  if (err instanceof Error) return err.message;
  return String(err);
}

function isTooExpensiveSqliteError(err: unknown) {
  return sqliteErrorMessage(err).includes("disk I/O error");
}

async function checkSqliteUrl(url: string) {
  const res = await fetch(openUrl(url), {
    method: "HEAD",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`File check failed with HTTP ${res.status}.`);
  }
  if (res.headers.get("Accept-Ranges") !== "bytes") {
    throw new Error("File server does not support byte ranges.");
  }
}

function sqliteConfig(url: string): SplitFileConfig[] {
  return [
    {
      from: "inline",
      config: {
        serverMode: "full",
        requestChunkSize: SQLITE_REQUEST_CHUNK_SIZE,
        url,
      },
    },
  ];
}

// We talk to the sql.js-httpvfs worker bundle directly instead of through its
// createDbWorker, which hides the Worker handle and so can never be torn down.
// The worker sends the database back under this transfer handler name, and our
// comlink is pinned to the bundle's wire protocol (see assets/sqlite/protocol.test.ts).
transferHandlers.set("WORKERSQLPROXIES", {
  canHandle: (_value): _value is unknown => false,
  serialize() {
    throw new Error("not used");
  },
  deserialize: (port) => {
    const dbPort = port as MessagePort;
    dbPort.start();
    return wrap(dbPort);
  },
});

async function createSqliteWorker(url: string): Promise<SqliteWorker> {
  const [{ default: InlineSqliteWorker }, { default: sqliteWasmUrl }] =
    await Promise.all([
      import("../assets/sqlite/generated/sqlite.worker.js?worker&inline"),
      import("../assets/sqlite/generated/sql-wasm.wasm?url"),
    ]);
  const worker = new InlineSqliteWorker();
  try {
    const sqlite = wrap<SqliteComlinkMod>(worker);
    const db = (await sqlite.SplitFileHttpDatabase(
      sqliteWasmUrl,
      sqliteConfig(url),
      undefined,
      SQLITE_MAX_BYTES_TO_READ
    )) as unknown as Remote<LazyHttpDatabase>;
    return { db, terminate: () => worker.terminate() };
  } catch (err) {
    worker.terminate();
    throw err;
  }
}

function useSqliteDatabase(enabled: boolean, url: string | null) {
  const workerRef = useRef<SqliteWorker | null>(null);
  const [state, setState] = useState<DatabaseState>({ status: "idle" });

  useEffect(() => {
    if (!enabled) return;
    if (!url) {
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setState({ status: "missing-url" });
      return;
    }

    let cancelled = false;
    const previewUrl = sqlitePreviewUrl(url);
    setState({ status: "loading" });
    checkSqliteUrl(previewUrl)
      .then(() => createSqliteWorker(previewUrl))
      .then(async (worker) => {
        if (cancelled) {
          worker.terminate();
          return;
        }

        workerRef.current = worker;
        try {
          const schema = await readSchema(worker.db);
          if (cancelled) {
            worker.terminate();
            return;
          }

          setState({ status: "ready", schema });
        } catch (err) {
          if (cancelled) return;
          worker.terminate();
          workerRef.current = null;
          setState({
            ...(isTooExpensiveSqliteError(err)
              ? { status: "too-expensive" as const }
              : {
                  status: "error" as const,
                  message: sqliteErrorMessage(err),
                }),
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          ...(isTooExpensiveSqliteError(err)
            ? { status: "too-expensive" as const }
            : {
                status: "error" as const,
                message: sqliteErrorMessage(err),
              }),
        });
      });

    return () => {
      cancelled = true;
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [enabled, url]);

  return { state, workerRef };
}

function useTableResult(
  workerRef: React.RefObject<SqliteWorker | null>,
  table: SqlTable | null,
  searchColumn: string | null,
  search: string,
  page: number
) {
  const [state, setState] = useState<ResultState>({ status: "idle" });

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker || !table) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });
    queryTable(worker.db, table, searchColumn, search, page)
      .then((result) => {
        if (!cancelled) setState({ status: "ready", result });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          ...(isTooExpensiveSqliteError(err)
            ? { status: "too-expensive" as const }
            : {
                status: "error" as const,
                message: sqliteErrorMessage(err),
              }),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [workerRef, table, searchColumn, search, page]);

  return state;
}

function columnTypeInfo(column: SqlColumn) {
  return [
    column.type || "No type",
    column.primaryKey ? "primary key" : "",
    column.notNull ? "not null" : "",
  ]
    .filter(Boolean)
    .join(META_SEP);
}

export function SqlitePreview({
  enabled,
  url,
}: {
  enabled: boolean;
  url: string | null;
}) {
  const { state, workerRef } = useSqliteDatabase(enabled, url);
  const [activeTableName, setActiveTableName] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchColumn, setSearchColumn] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const schema = state.status === "ready" ? state.schema : null;
  const tables = schema?.tables ?? [];
  const activeTable =
    tables.find((table) => table.name === activeTableName) ?? tables[0] ?? null;
  const resultState = useTableResult(
    workerRef,
    activeTable,
    searchColumn,
    search,
    page
  );

  useEffect(() => {
    if (activeTable && activeTable.name !== activeTableName) {
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setActiveTableName(activeTable.name);
      setSearch("");
      setSearchColumn(null);
      setPage(0);
    }
  }, [activeTable, activeTableName]);

  if (state.status === "idle" || state.status === "loading")
    return <TableLoading />;
  if (state.status === "missing-url") {
    return <TableStatus>SQLite range preview needs a file URL.</TableStatus>;
  }
  if (state.status === "too-expensive") {
    return (
      <TableStatus>
        This query read more than {formatBytes(SQLITE_MAX_BYTES_TO_READ)}.
      </TableStatus>
    );
  }
  if (state.status === "error") {
    return (
      <TableStatus>Unable to open SQLite database: {state.message}</TableStatus>
    );
  }
  if (!schema) return <TableLoading />;
  if (!activeTable) return <TableStatus>No tables found.</TableStatus>;

  const result = resultState.status === "ready" ? resultState.result : null;
  const columnsByName = new Map(
    activeTable.columns.map((column) => [column.name, column] as const)
  );
  const hasPrevious = page > 0;
  const hasNext = result?.hasNext ?? false;

  const status =
    resultState.status === "loading" ? (
      <TableLoading />
    ) : resultState.status === "error" ? (
      <TableStatus>Unable to query table.</TableStatus>
    ) : resultState.status === "too-expensive" ? (
      <TableStatus>
        This query read more than {formatBytes(SQLITE_MAX_BYTES_TO_READ)}.
      </TableStatus>
    ) : result && result.rows.length === 0 ? (
      <TableStatus>No rows found.</TableStatus>
    ) : undefined;

  return (
    <TablePreview
      tables={tables.map((table) => ({
        name: table.name,
        icon: table.type === "view" ? "layers" : "table",
      }))}
      activeTable={activeTable.name}
      onSelectTable={(name) => {
        setActiveTableName(name);
        setSearch("");
        setSearchColumn(null);
        setPage(0);
      }}
      searchColumns={activeTable.columns.map((column) => column.name)}
      searchColumn={searchColumn}
      onSearchColumn={(name) => {
        setSearchColumn(name);
        setPage(0);
      }}
      search={search}
      onSearch={(value) => {
        setSearch(value);
        setPage(0);
      }}
      columns={(result?.columns ?? []).map((name) => {
        const info = columnsByName.get(name);
        return { name, tooltip: info ? columnTypeInfo(info) : undefined };
      })}
      rows={(result?.rows ?? []).map((row) => ({
        key: row.key,
        values: Object.fromEntries(
          (result?.columns ?? []).map((name) => [
            name,
            formatSqlValue(row.values[name] ?? null),
          ])
        ),
      }))}
      status={status}
      page={page}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      onPage={setPage}
    />
  );
}
